import React from 'react'
import { useTranslation } from 'react-i18next'

import { TreeTableSelectionKeysType } from 'primereact/treetable'

import { useToastContext } from '../../../../../../../contexts'
import { getHandleableError } from '../../../../../../../utils/errors/isXiaError'
import { requestDeleteDeliveryForecastDespatchAdviceItemPackagingItem } from '../../../../../data/api'
import { DeleteView } from './Delete.view'

type Props = {
  despatchAdviceId: string
  itemId: string
  packagingId: string
  ids: string[]
  setIds: React.Dispatch<React.SetStateAction<string[]>>
  keys: TreeTableSelectionKeysType
  setKeys: React.Dispatch<React.SetStateAction<TreeTableSelectionKeysType>>
  setIsRequestRunning: React.Dispatch<React.SetStateAction<boolean>>
  isVisible: boolean
  onHide: () => void
}

export const DeleteData: React.FC<Props> = ({
  despatchAdviceId,
  itemId,
  packagingId,
  ids,
  setIds,
  keys,
  setKeys,
  setIsRequestRunning,
  isVisible,
  onHide,
}) => {
  const { t } = useTranslation()
  const { showToast } = useToastContext()

  const idsLength = ids.length

  const handleDelete = async () => {
    try {
      setIsRequestRunning(true)

      await submitDelete()

      onHide()

      showToast({
        severity: 'success',
        summary: t(
          'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.delete.dialog.delete.messages.success.summary',
          {
            count: idsLength,
          },
        ),
        detail: t(
          'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.delete.dialog.delete.messages.success.detail',
          {
            count: idsLength,
          },
        ),
      })
    } catch (error) {
      const handleableError = getHandleableError(error)
      const errorMessage = handleableError.message

      showToast({
        severity: 'error',
        summary: t(
          'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.delete.dialog.delete.messages.failure.summary',
        ),
        detail: errorMessage,
        sticky: true,
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  const submitDelete = async () => {
    if (idsLength === 0) return

    await Promise.all(
      ids.map(id =>
        requestDeleteDeliveryForecastDespatchAdviceItemPackagingItem(
          despatchAdviceId,
          itemId,
          packagingId,
          id,
        ),
      ),
    )

    // Remove deleted ids from selected ids
    setIds(prevIds => handleSetIds(prevIds, ids, keys))

    // Remove keys from selected keys
    setKeys(prevKeys => handleSetKeys(prevKeys, ids))
  }

  return (
    <DeleteView
      handleDelete={handleDelete}
      length={idsLength}
      isVisible={isVisible}
      onHide={onHide}
    />
  )
}

/**
 * Removes ids from the given selection based on the provided ids to delete.
 *
 * @param prevIds - The previous selection ids.
 * @param idsToDelete - An array of ids whose corresponding keys need to be deleted.
 * @param keys - An object of keys whose corresponding ids need to be deleted.
 * @returns A new selection ids array without the deleted ids.
 */
const handleSetIds = (
  prevIds: string[],
  idsToDelete: string[],
  keys: TreeTableSelectionKeysType,
): string[] => {
  let newIds = prevIds.filter(id => !idsToDelete.includes(id))

  // We check if we delete a parent which is not selected and delete all children that are selected
  let childIds: string[] = []
  for (const idToDelete of idsToDelete) {
    for (const key in keys) {
      if (key.startsWith(idToDelete)) {
        const childId = key.replace(`${idToDelete}-`, '')
        childIds.push(childId)
      }
    }
  }

  newIds = newIds.filter(id => !childIds.includes(id))

  return newIds
}

/**
 * Removes keys from the given selection keys based on the provided ids to delete.
 *
 * @param prevKeys - The previous selection keys.
 * @param idsToDelete - An array of ids whose corresponding keys need to be deleted.
 * @returns A new selection keys object without the deleted keys.
 */
const handleSetKeys = (
  prevKeys: TreeTableSelectionKeysType,
  idsToDelete: string[],
): TreeTableSelectionKeysType => {
  const newKeys = { ...prevKeys }

  // Iterate through each id to remove matching keys
  idsToDelete.forEach(id => {
    for (const key in newKeys) {
      // If the key includes the id, delete it from newKeys
      if (key.includes(id)) {
        delete newKeys[key]
      }
    }
  })

  return newKeys
}
